import './update-bespoke-rfq-emails.scss'

import { notification } from 'antd'
import axios from 'axios'
import { Formik, FormikHelpers } from 'formik'
import { Form, FormItem, Input, Select, SubmitButton } from 'formik-antd'
import React, { useEffect, useState } from 'react'
import { Column, Row } from 'react-display-flex'

import { emptyCharacter } from '../../../../components/EmptyChar'
import { parseApiValidationErrors } from '../../../../components/forms/utils'
import { convertEntityValuesToFormValues } from '../../../../util/convert-entity-values-to-form-values'
import { Tenant } from '../../../Tenants/models'
import { messages } from '../../messages'
import { Issuer, RfqEmail, rfqEmailFormInitialValues, RfqEmailFormValues } from '../../models'
import { updateRfqEmailValidationSchema } from './validation'

interface Props {
  rfqEmail: RfqEmail
  issuers: Issuer[]
  tenants: Tenant[]
  onSuccess: () => void
}

export const UpdateBespokeRfqEmails = ({ rfqEmail, issuers, tenants, onSuccess }: Props) => {
  const [formInitialValues, setFormInitialValues] = useState<RfqEmailFormValues>(null)

  useEffect(() => {
    if (!rfqEmail) return

    const formValues = convertEntityValuesToFormValues(rfqEmailFormInitialValues, rfqEmail)
    formValues.email = splitEmails(rfqEmail.email)

    setFormInitialValues(formValues)
  }, [rfqEmail])

  const onSubmit = async (
    rfqEmailFormValues: RfqEmailFormValues,
    { setSubmitting, resetForm, setErrors }: FormikHelpers<RfqEmailFormValues>,
  ) => {
    const rfqEmailPayload = convertFormValuesToRfqEmailRequestPayload(rfqEmailFormValues)

    try {
      await axios.put('/rfqs/emails', rfqEmailPayload)

      resetForm()
      notification.success({ message: messages.common.success, description: messages.updateRfqEmail.success })
      onSuccess()
    } catch (error) {
      setErrors(parseApiValidationErrors(error))
      setSubmitting(false)
      notification.error({ message: messages.common.error, description: messages.updateRfqEmail.error })
    }
  }

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={updateRfqEmailValidationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      <Form className="update-rfq-email">
        <Row justifyContentSpaceBetween>
          <div>
            <dt>Issuer</dt>
            <dd>{issuers?.find(({ id }) => rfqEmail?.issuerId === id)?.shortName}</dd>
          </div>
          <div>
            <dt>Tenant</dt>
            <dd>{tenants?.find(({ id }) => rfqEmail?.tenantId === id)?.name || 'Any'}</dd>
          </div>
          <div>
            <dt>Instrument</dt>
            <dd>{rfqEmail?.instrumentCode === emptyCharacter ? 'Any' : rfqEmail?.instrumentCode}</dd>
          </div>
        </Row>
        <FormItem name="name" label="Name" required>
          <Input name="name" placeholder="Name" />
        </FormItem>
        <FormItem name="email" label="Emails" required>
          <Select name="email" placeholder="RFQ Emails" mode="tags" allowClear />
        </FormItem>
        <Column>
          <SubmitButton htmlType="submit" type="primary">
            Update RFQ Email
          </SubmitButton>
        </Column>
      </Form>
    </Formik>
  )
}

const splitEmails = (concatenatedEmails: string): string[] =>
  concatenatedEmails ? concatenatedEmails.split(',').map((email) => email.trim()) : []

const convertFormValuesToRfqEmailRequestPayload = (values: RfqEmailFormValues) => ({
  ...values,
  instrumentCode: values.instrumentCode === emptyCharacter ? undefined : values.instrumentCode,
  email: values.email.join(','),
})
